"use client";

import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport } from "ai";
import { ArrowLeftIcon, BotIcon, SendIcon, SquareIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { MessageResponse } from "@/components/ai-elements/message";
import type { ChatUIMessage } from "@/types/chat";

const TRANSPORT = new DefaultChatTransport({ api: "/api/policy-chat" });

const SUGGESTIONS = [
  "What AI tools am I approved to use?",
  "Can I share client information with AI?",
  "What should I never put into an AI tool?",
  "What is the difference between Regular and Power Users?",
];

type PolicyPanelProps = {
  onBack: () => void;
};

const MIN_CHAT_WIDTH = 260;
const MAX_CHAT_WIDTH = 640;
const DEFAULT_CHAT_WIDTH = 384;

export function PolicyPanel({ onBack }: PolicyPanelProps) {
  const { messages, sendMessage, status, stop } = useChat<ChatUIMessage>({
    transport: TRANSPORT,
  });
  const [input, setInput] = useState("");
  const isStreaming = status === "streaming" || status === "submitted";
  const bottomRef = useRef<HTMLDivElement>(null);
  const prevLengthRef = useRef(0);
  const [chatWidth, setChatWidth] = useState(DEFAULT_CHAT_WIDTH);
  const containerRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);

  useEffect(() => {
    const newLength = messages.length;
    const shouldScroll =
      newLength > prevLengthRef.current && prevLengthRef.current > 0;
    prevLengthRef.current = newLength;
    if (shouldScroll) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  const handleDividerMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    isDraggingRef.current = true;

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDraggingRef.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const newWidth = rect.right - e.clientX;
      setChatWidth(Math.max(MIN_CHAT_WIDTH, Math.min(MAX_CHAT_WIDTH, newWidth)));
    };

    const handleMouseUp = () => {
      isDraggingRef.current = false;
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleSend = (text?: string) => {
    const msg = (text ?? input).trim();
    if (!msg || isStreaming) return;
    setInput("");
    sendMessage({ text: msg });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      {/* Header */}
      <div className="shrink-0 border-b border-border">
        <div className="px-6 pt-6 pb-2">
          <Button
            className="gap-2 text-muted-foreground"
            onClick={onBack}
            size="sm"
            type="button"
            variant="ghost"
          >
            <ArrowLeftIcon className="size-4" />
            Back
          </Button>
        </div>
        <div className="px-6 pb-5">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            AI Policy
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Woods AI Policy Statement &amp; Usage Guidelines — April 2026
          </p>
        </div>
      </div>

      {/* Body */}
      <div ref={containerRef} className="flex min-h-0 flex-1 overflow-hidden">
        {/* Policy content */}
        <ScrollArea className="min-h-0 flex-1 overflow-hidden">
          <div className="mx-auto max-w-3xl px-10 py-10 text-sm">
            <PolicyContent />
          </div>
        </ScrollArea>

        {/* Drag handle */}
        <div
          className="relative w-px shrink-0 cursor-col-resize bg-border hover:bg-primary/40 transition-colors"
          onMouseDown={handleDividerMouseDown}
        >
          <div className="absolute inset-y-0 -left-1.5 -right-1.5" />
        </div>

        {/* Chat bot */}
        <div
          className="flex min-h-0 shrink-0 flex-col overflow-hidden"
          style={{ width: chatWidth }}
        >
          <div className="flex items-center gap-2 border-b border-border px-4 py-3">
            <BotIcon className="size-4 text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">
              Ask the Policy Bot
            </span>
            <span className="ml-auto text-[10px] text-muted-foreground">
              Woods AI · Always Incognito
            </span>
          </div>

          {/* Messages */}
          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 py-4 bg-muted/20">
            {messages.length === 0 ? (
              <div className="flex flex-col gap-3">
                <p className="text-xs text-muted-foreground">
                  Ask anything about the Woods AI Policy. Try one of these:
                </p>
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => handleSend(s)}
                    className="rounded-xl border border-border bg-background px-3 py-2.5 text-left text-xs text-foreground transition-colors hover:bg-muted"
                  >
                    {s}
                  </button>
                ))}
              </div>
            ) : (
              messages.map((msg) => {
                const text = msg.parts
                  .filter((p) => p.type === "text")
                  .map((p) => (p as { type: "text"; text: string }).text)
                  .join("");
                if (!text) return null;
                const isUser = msg.role === "user";
                return (
                  <div
                    key={msg.id}
                    className={cn(
                      "flex gap-2",
                      isUser ? "justify-end" : "justify-start"
                    )}
                  >
                    {!isUser ? (
                      <div className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-foreground text-[9px] font-semibold text-background">
                        W
                      </div>
                    ) : null}
                    <div
                      className={cn(
                        "max-w-[88%] rounded-2xl px-3.5 py-2 text-xs leading-relaxed",
                        isUser
                          ? "bg-secondary text-foreground rounded-br-sm"
                          : "bg-background border border-border text-foreground rounded-bl-sm"
                      )}
                    >
                      <MessageResponse
                        isAnimating={
                          isStreaming && msg === messages[messages.length - 1]
                        }
                        disallowedElements={["script", "style"]}
                      >
                        {text}
                      </MessageResponse>
                    </div>
                    {isUser ? (
                      <div className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-muted text-[9px] font-semibold text-muted-foreground">
                        You
                      </div>
                    ) : null}
                  </div>
                );
              })
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div className="flex items-end gap-2 border-t border-border bg-background px-3 py-2">
            <textarea
              className="flex-1 resize-none bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none py-1.5 min-h-9 max-h-24"
              placeholder="Ask about the AI policy…"
              rows={1}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isStreaming}
            />
            {isStreaming ? (
              <Button
                size="icon"
                variant="ghost"
                className="size-7 shrink-0 text-muted-foreground hover:text-foreground"
                onClick={stop}
                type="button"
              >
                <SquareIcon className="size-3.5" />
              </Button>
            ) : (
              <Button
                size="icon"
                variant="ghost"
                className="size-7 shrink-0 text-muted-foreground hover:text-foreground disabled:opacity-30"
                onClick={() => handleSend()}
                disabled={!input.trim()}
                type="button"
              >
                <SendIcon className="size-3.5" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function PolicyContent() {
  return (
    <div className="flex flex-col gap-8">
      {/* Policy Statement */}
      <section>
        <h2 className="text-xl font-semibold text-foreground mb-4">
          AI Policy Statement
        </h2>

        <div className="flex flex-col gap-5">
          <div>
            <h3 className="font-semibold text-foreground mb-1.5">Intent</h3>
            <p className="text-muted-foreground leading-relaxed">
              Artificial Intelligence (AI) is a key ingredient in maintaining
              and extending Woods&apos; position as a leading professional services
              business delivering land enablement projects across engineering,
              surveying, planning and urban design and geospatial services. It
              provides us with the potential to create new services for clients,
              streamline how we deliver client work, and remove unproductive
              administrative tasks from our daily schedules.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-foreground mb-1.5">
              Expectation
            </h3>
            <p className="text-muted-foreground leading-relaxed">
              AI will become a core tool for how we work and deliver value to
              our clients. All Woods staff will be expected to actively adopt AI,
              as the technology evolves and as Woods&apos; capability expands. Woods
              wants to encourage the thoughtful use of AI whilst safeguarding
              quality, privacy, and professionalism.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-foreground mb-1.5">
              Supporting our People
            </h3>
            <p className="text-muted-foreground leading-relaxed">
              Woods wants to ensure AI supports your work effectively,
              responsibly, and in line with our professional standards,
              contractual and legal obligations. To enable this, Woods will
              endeavour to provide the most relevant and up to date guidance,
              policies, training and management support.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-2">
              Woods has established a dedicated Data and AI team to lead the
              rollout of Woods-centric AI solutions. Woods has also appointed AI
              Champions from each area of the business to gather feedback from
              end users and to support AI rollout and uptake.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-foreground mb-3">
              Core Principles
            </h3>
            <div className="flex flex-col gap-2">
              {[
                {
                  n: "1",
                  title: "Leadership",
                  desc: "We encourage our people to adopt an AI-first mindset and to actively seek out AI-centric opportunities.",
                },
                {
                  n: "2",
                  title: "Transparency",
                  desc: "We encourage our people to share how they're using AI so we can collectively learn.",
                },
                {
                  n: "3",
                  title: "Protect information",
                  desc: "Client and Woods information must be handled safely, responsibly and in line with our contractual and legal obligations. Only Woods approved AI tools should be used for Woods work.",
                },
                {
                  n: "4",
                  title: "Quality and safety first",
                  desc: "AI can be wrong, biased, or incomplete. Treat it like a tool, and always ensure there is a human-in-the-loop whenever you are using AI.",
                },
                {
                  n: "5",
                  title: "You remain accountable",
                  desc: "AI supports your work; it does not replace professional judgement or expertise.",
                },
              ].map(({ n, title, desc }) => (
                <div
                  key={n}
                  className="flex gap-3 rounded-lg border border-border bg-muted/30 px-4 py-3"
                >
                  <span className="shrink-0 mt-0.5 flex size-5 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">
                    {n}
                  </span>
                  <div>
                    <span className="font-semibold text-foreground">
                      {title}.
                    </span>{" "}
                    <span className="text-muted-foreground">{desc}</span>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-4 text-xs text-muted-foreground">
              Approved by Chief Executive Officer — David Kidd · April 2026
            </p>
          </div>
        </div>
      </section>

      <hr className="border-border" />

      {/* Usage Guidelines */}
      <section>
        <h2 className="text-xl font-semibold text-foreground mb-1">
          AI Usage Guidelines
        </h2>
        <p className="text-xs text-muted-foreground mb-6">
          As of April 2026 — reviewed periodically as the AI landscape evolves.
        </p>

        <div className="flex flex-col gap-8">
          <GuidelineSection
            number="1"
            title="Leadership"
            content={
              <div className="flex flex-col gap-3 text-muted-foreground leading-relaxed">
                <p>
                  Woods wants to remain at the forefront of AI and integrate it
                  into our business as and when it makes sense. For Woods to
                  successfully leverage AI, we need all employees to play their
                  part by embracing an AI-first mindset.
                </p>
                <p>
                  We acknowledge that for some employees, AI might seem
                  daunting. Whatever your level of expertise, we encourage you
                  to start using AI in your day-to-day work, and to build your
                  confidence with the tools provided.
                </p>
                <p>
                  We encourage employees to think about how AI could streamline
                  existing workflows. Share your ideas with your AI Champion, or
                  with the Data and AI Team.
                </p>
              </div>
            }
          />

          <GuidelineSection
            number="2"
            title="Transparency"
            content={
              <div className="flex flex-col gap-3 text-muted-foreground leading-relaxed">
                <p>
                  We genuinely want to encourage AI use, not keep it in the
                  shadows. If you&apos;re using AI tools for any work-related task,
                  be upfront internally about how and where you&apos;re using it.
                </p>
                <p>
                  If AI materially contributes to a client deliverable (report,
                  advice, plan, design, drawing, submission, email advice), you
                  should be able to explain how it was used and what checks you
                  performed. Whoever is quality assuring your work should know
                  if AI has been involved.
                </p>
              </div>
            }
          />

          <GuidelineSection
            number="3"
            title="Protect information"
            content={
              <div className="flex flex-col gap-5">
                <p className="text-muted-foreground leading-relaxed">
                  Be careful with the information you share with AI, to protect
                  Woods and client interests, and stick to our approved AI tools
                  to avoid potential leakage of privileged or sensitive
                  information.
                </p>

                <div>
                  <p className="font-medium text-foreground mb-2">
                    Client information
                  </p>
                  <ul className="flex flex-col gap-1.5 text-muted-foreground">
                    {[
                      "Confirm whether your client places any restrictions on the use of AI or the sharing of information.",
                      "If unsure, check with your manager or project manager before proceeding.",
                      "Where you become aware of specific client requirements, share them with the Data and AI Team.",
                    ].map((item) => (
                      <li key={item} className="flex gap-2">
                        <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-muted-foreground/50" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <p className="font-medium text-foreground mb-2">
                    You can enter into approved tools
                  </p>
                  <ul className="flex flex-col gap-1.5 text-muted-foreground">
                    {[
                      "Client information — except where clients have specified restrictions.",
                      "Non-public Woods documents, designs, or datasets.",
                      "Third-party copyrighted content (e.g., other consultant reports) which we have permission to use.",
                    ].map((item) => (
                      <li key={item} className="flex gap-2">
                        <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-green-500" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <p className="font-medium text-foreground mb-2">
                    Never put into any AI tool
                  </p>
                  <ul className="flex flex-col gap-1.5 text-muted-foreground">
                    {[
                      "Passwords, API keys, credentials, tokens, certificates, credit card details.",
                      "Personal information about yourself or others (e.g., age, home address, driver license, DOB, passport, etc.).",
                    ].map((item) => (
                      <li key={item} className="flex gap-2">
                        <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-destructive" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Approved tools table */}
                <div>
                  <p className="font-medium text-foreground mb-3">
                    Approved tools
                  </p>
                  <div className="w-full overflow-hidden rounded-xl border border-border">
                    <table className="w-full text-xs">
                      <thead>
                        <tr>
                          <th className="border-b border-r border-border bg-primary px-4 py-2.5 text-left font-semibold text-primary-foreground w-28" />
                          <th className="border-b border-r border-border bg-primary px-4 py-2.5 text-left font-semibold text-primary-foreground">
                            Approved Tools
                          </th>
                          <th className="border-b border-border bg-primary px-4 py-2.5 text-left font-semibold text-primary-foreground">
                            Not Approved
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="align-top">
                          <td className="border-b border-r border-border px-4 py-3 font-semibold text-foreground">
                            Regular Users
                          </td>
                          <td className="border-b border-r border-border px-4 py-3 text-muted-foreground">
                            <ul className="flex flex-col gap-2">
                              <li>
                                <span className="font-medium text-foreground">
                                  Woods AI
                                </span>{" "}
                                (browser-based, ai.woods.co.nz) — available to
                                all employees, designed for secure Woods-specific
                                use. The home for your knowledge bases.
                              </li>
                              <li>
                                <span className="font-medium text-foreground">
                                  Microsoft Copilot
                                </span>{" "}
                                — via our Woods corporate Microsoft suite.
                              </li>
                            </ul>
                          </td>
                          <td
                            className="border-b border-border px-4 py-3 text-muted-foreground"
                            rowSpan={2}
                          >
                            <ul className="flex flex-col gap-2">
                              <li>
                                Personal AI tools (free or paid) for Woods work.
                              </li>
                              <li>
                                Claude desktop app — can access system files and
                                directories.
                              </li>
                              <li>
                                Any other AI apps, plug-ins, extensions, local
                                models, or agents without IT approval (e.g.,
                                ChatGPT Atlas browser, DeepSeek, Grok).
                              </li>
                            </ul>
                          </td>
                        </tr>
                        <tr className="align-top">
                          <td className="border-r border-border px-4 py-3 font-semibold text-foreground">
                            Power Users
                            <p className="mt-1 text-[10px] font-normal text-muted-foreground">
                              (same as Regular, plus)
                            </p>
                          </td>
                          <td className="border-r border-border px-4 py-3 text-muted-foreground">
                            <ul className="flex flex-col gap-2">
                              <li>
                                <span className="font-medium text-foreground">
                                  ChatGPT via Woods Enterprise Plan
                                </span>{" "}
                                (browser-based or desktop app) — for select
                                users with requirements not met by Woods AI.
                              </li>
                              <li>
                                <span className="font-medium text-foreground">
                                  Claude via Woods Enterprise Plan
                                </span>{" "}
                                (browser-based only) — for select users with
                                requirements not met by Woods AI.
                              </li>
                            </ul>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">
                    A small &ldquo;Super Users&rdquo; group may be granted exceptions to
                    trial new tools.
                  </p>
                </div>
              </div>
            }
          />

          <GuidelineSection
            number="4"
            title="Quality and safety first"
            content={
              <div className="flex flex-col gap-3 text-muted-foreground leading-relaxed">
                <p>
                  All AI outputs must be reviewed and validated before use in a
                  client deliverable or internal decision-making document. AI
                  doesn&apos;t understand context or nuances — you do. Do not use AI
                  to create final technical designs or sign-off material without
                  appropriate discipline review and required approvals.
                </p>
                <p>
                  You must check accuracy, completeness, assumptions,
                  calculations, references/standards, and alignment with client
                  context. AI must not be used to justify decisions without
                  evidence. If you can&apos;t verify it, don&apos;t use it.
                </p>
              </div>
            }
          />

          <GuidelineSection
            number="5"
            title="You remain accountable"
            content={
              <ul className="flex flex-col gap-2 text-muted-foreground">
                {[
                  "AI must be used in a safe and responsible way in line with our contractual and legal obligations.",
                  "Share what you're doing on AI so everyone can learn and improve capability.",
                  "AI is here to assist, not replace your professional judgement.",
                  "You're the expert — AI provides support, not decisions.",
                ].map((item) => (
                  <li key={item} className="flex gap-2 leading-relaxed">
                    <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-muted-foreground/50" />
                    {item}
                  </li>
                ))}
              </ul>
            }
          />
        </div>
      </section>

      <hr className="border-border" />

      {/* Support */}
      <section>
        <h2 className="text-xl font-semibold text-foreground mb-1">Support</h2>
        <p className="text-xs text-muted-foreground mb-6">
          Get help with AI tools and policies at Woods.
        </p>

        <div className="flex flex-col gap-8">
          {/* AI Champions */}
          <div>
            <h3 className="text-base font-semibold text-foreground mb-2">
              AI Champions – Front Line Support
            </h3>
            <p className="text-muted-foreground leading-relaxed mb-4">
              For general enquiries, connect with your team&apos;s AI Champion.
              They can help with approved AI tool setup, prompting guidance,
              Knowledge Agent configuration, and policy questions.
            </p>
            <div className="w-full overflow-hidden rounded-xl border border-border">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-muted/50">
                    <th className="border-b border-r border-border px-4 py-2.5 text-left font-semibold text-foreground">
                      Name
                    </th>
                    <th className="border-b border-r border-border px-4 py-2.5 text-left font-semibold text-foreground">
                      Email
                    </th>
                    <th className="border-b border-border px-4 py-2.5 text-left font-semibold text-foreground">
                      Team
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {[
                    { name: "Guy Pryor", email: "guy.pryor@woods.co.nz", team: "Engineering" },
                    { name: "Tim Rickards", email: "tim.rickards@woods.co.nz", team: "Engineering" },
                    { name: "Glenn Lloyd", email: "glenn.lloyd@woods.co.nz", team: "Engineering" },
                    { name: "Abdullah Bhatti", email: "abdullah.bhatti@woods.co.nz", team: "Engineering" },
                    { name: "Cristian Jara", email: "cristian.jara@woods.co.nz", team: "Water Infrastructure & Planning" },
                    { name: "Steven Haslemore", email: "steven.haslemore@woods.co.nz", team: "Geospatial" },
                    { name: "Bond Cen", email: "bond.cen@woods.co.nz", team: "Surveying" },
                    { name: "Fergus McArthur", email: "fergus.mcarthur@woods.co.nz", team: "Planning & Urban Design" },
                    { name: "Ella McPherson", email: "ella.mcpherson@woods.co.nz", team: "Commercial & Operations" },
                    { name: "Andrew Norman", email: "andrew.norman@woods.co.nz", team: "IT" },
                  ].map(({ name, email, team }) => (
                    <tr key={email}>
                      <td className="border-r border-border px-4 py-2.5 font-medium text-foreground">
                        {name}
                      </td>
                      <td className="border-r border-border px-4 py-2.5">
                        <a
                          href={`mailto:${email}`}
                          className="text-primary hover:underline"
                        >
                          {email}
                        </a>
                      </td>
                      <td className="px-4 py-2.5 text-muted-foreground">
                        {team}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Technical support */}
          <div>
            <h3 className="text-base font-semibold text-foreground mb-2">
              Software and Technical Issues
            </h3>
            <p className="text-muted-foreground leading-relaxed mb-3">
              For technical support including the Woods AI webpage, platform
              access, troubleshooting, software bugs, integrations, or urgent
              security incidents — email{" "}
              <a
                href="mailto:ai@woods.co.nz"
                className="text-primary hover:underline"
              >
                ai@woods.co.nz
              </a>{" "}
              or contact the Data &amp; AI team directly:
            </p>
            <div className="w-full overflow-hidden rounded-xl border border-border">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-muted/50">
                    <th className="border-b border-r border-border px-4 py-2.5 text-left font-semibold text-foreground">
                      Name
                    </th>
                    <th className="border-b border-border px-4 py-2.5 text-left font-semibold text-foreground">
                      Email
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {[
                    { name: "Matt Orange", email: "matthew.orange@woods.co.nz" },
                    { name: "Lucia Katkova", email: "lucia.katkova@woods.co.nz" },
                    { name: "Amit Philip", email: "amit.philip@woods.co.nz" },
                    { name: "Saranya Kumar", email: "saranya.kumar@woods.co.nz" },
                  ].map(({ name, email }) => (
                    <tr key={email}>
                      <td className="border-r border-border px-4 py-2.5 font-medium text-foreground">
                        {name}
                      </td>
                      <td className="px-4 py-2.5">
                        <a
                          href={`mailto:${email}`}
                          className="text-primary hover:underline"
                        >
                          {email}
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Governance */}
          <div>
            <h3 className="text-base font-semibold text-foreground mb-2">
              AI &amp; Data Governance &amp; Policy
            </h3>
            <p className="text-muted-foreground leading-relaxed mb-3">
              Contact{" "}
              <a
                href="mailto:matthew.orange@woods.co.nz"
                className="text-primary hover:underline"
              >
                Matt Orange
              </a>{" "}
              (GM of Data &amp; AI) for:
            </p>
            <ul className="flex flex-col gap-1.5 text-muted-foreground">
              {[
                "Policy interpretation and clarification",
                "Compliance questions",
                "Governance matters",
                "Policy exceptions or special requests",
                "Regulatory guidance",
                "Requesting superuser privileges",
                "Requesting paid tool access",
              ].map((item) => (
                <li key={item} className="flex gap-2">
                  <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-muted-foreground/50" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}

function GuidelineSection({
  number,
  title,
  content,
}: {
  number: string;
  title: string;
  content: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-center gap-2.5 mb-4">
        <span className="flex size-6 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground shrink-0">
          {number}
        </span>
        <h3 className="text-base font-semibold text-foreground">{title}</h3>
      </div>
      <div className="pl-8.5">{content}</div>
    </div>
  );
}
